import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDate, formatINR } from "@/lib/constants";
import { Mail, Calendar, ClipboardList, User, ShieldCheck } from "lucide-react";

const NOTE_LABELS = {
  health_insurance: "Health Insurance",
  term_insurance: "Term Insurance",
  emergency_fund: "Emergency Fund",
  existing_portfolio: "Existing Portfolio",
  help_required: "Help Required",
  basic_goals: "Basic Goals",
  next_steps: "Next Steps",
};

export default function DiscoveryCallTab({ clientId }) {
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/clients/${clientId}/discovery-call`).then(r => setBundle(r.data)).catch(() => setBundle({ linked: false, message: "Failed to load." })).finally(() => setLoading(false));
  }, [clientId]);

  if (loading) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  if (!bundle?.linked) {
    return (
      <Card className="border-border p-8 text-center" data-testid="discovery-empty">
        <p className="text-sm text-muted-foreground">{bundle?.message || "No discovery call record is linked to this client."}</p>
      </Card>
    );
  }

  const p = bundle.prospect;
  const notes = bundle.summary_notes;
  const em = bundle.summary_email;
  const primary = bundle.primary_appointment;
  const otherAppts = (bundle.appointments || []).filter(a => a.id !== primary?.id);
  const hi = p.current_insurance_health || {};
  const li = p.current_insurance_life || {};

  return (
    <div className="space-y-6" data-testid="discovery-tab">
      <div className="rounded-md border border-primary/20 bg-primary/5 p-3 text-xs text-muted-foreground">
        This is a <b>read-only historical record</b> from before conversion — <i>as recorded / as submitted on the noted date</i>. Current details live in the Profile, Family and Insurance tabs.
      </div>

      <Card className="border-border p-5" data-testid="call-notes">
        <h3 className="mb-3 flex items-center gap-2 font-dashboard text-sm font-bold"><ClipboardList className="h-4 w-4 text-primary" /> Discovery Call Notes</h3>
        {notes && Object.values(notes).some(v => (v || "").trim()) ? (
          <div className="space-y-3">
            {Object.entries(NOTE_LABELS).map(([k, label]) => (notes[k] || "").trim() && (
              <div key={k}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">{label}</p>
                <p className="mt-1 whitespace-pre-line text-sm text-foreground/80">{notes[k]}</p>
              </div>
            ))}
            <p className="pt-3 text-[10px] text-muted-foreground border-t border-border">Entered {bundle.summary_entered_at ? formatDate(bundle.summary_entered_at) : "—"}</p>
          </div>
        ) : <p className="text-sm text-muted-foreground">No call notes captured for this client.</p>}
      </Card>

      <Card className="border-border p-5" data-testid="summary-email">
        <h3 className="mb-3 flex items-center gap-2 font-dashboard text-sm font-bold"><Mail className="h-4 w-4 text-primary" /> Summary Email {em?.status && <Badge variant="secondary" className="ml-2 text-[10px]">{em.status}</Badge>}</h3>
        {em ? (
          <div>
            <div className="mb-2 space-y-0.5 text-xs text-muted-foreground" data-testid="summary-email-meta">
              {em.subject && <div><b>Subject:</b> {em.subject}</div>}
              {em.to && <div><b>To:</b> {em.to}</div>}
              {em.sent_at && <div><b>Sent:</b> {formatDate(em.sent_at)}</div>}
            </div>
            {em.body ? (
              <pre
                className="whitespace-pre-wrap break-words rounded-md border border-border bg-muted/40 p-3 font-sans text-sm leading-relaxed text-foreground/90"
                data-testid="summary-email-body"
              >{em.body}</pre>
            ) : (
              <p className="text-xs text-muted-foreground">No body captured for this summary.</p>
            )}
          </div>
        ) : <p className="text-sm text-muted-foreground">No summary email was sent.</p>}
      </Card>

      <Card className="border-border p-5" data-testid="as-submitted">
        <h3 className="mb-3 flex items-center gap-2 font-dashboard text-sm font-bold"><User className="h-4 w-4 text-primary" /> Details as Submitted by the Prospect <span className="ml-2 text-[10px] font-normal text-muted-foreground">as submitted on {formatDate(p.created_at)}</span></h3>
        <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
          <Field label="Full Name" value={p.full_name} />
          <Field label="Mobile" value={p.mobile} />
          <Field label="Email" value={p.email} />
          <Field label="Occupation" value={p.occupation} />
          <Field label="Age" value={p.age} />
          <Field label="Marital Status" value={p.marital_status} />
          <Field label="Monthly income" value={formatINR(p.monthly_income)} />
          <Field label="Monthly expenditure" value={formatINR(p.monthly_expenditure)} />
          <Field label="Surplus to invest" value={formatINR(p.surplus_available_to_invest)} />
        </div>
        <div className="mt-4 border-t border-border pt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Financial goals</p>
          <p className="mt-1 text-sm">{(p.financial_goals || []).join(", ") || "—"}</p>
        </div>
        <div className="mt-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Current investments summary</p>
          <p className="mt-1 whitespace-pre-line text-sm text-foreground/80">{p.current_investments_summary || "—"}</p>
        </div>
        <div className="mt-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Expectations from us</p>
          <p className="mt-1 whitespace-pre-line text-sm text-foreground/80">{p.expectations_from_us || "—"}</p>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div className="rounded-md border border-border p-3">
            <p className="text-[11px] font-semibold text-primary">Health cover</p>
            {hi.has_cover ? <p className="mt-1 text-sm">{hi.insurer || "?"} · {formatINR(hi.sum_insured)}</p> : <p className="mt-1 text-sm text-muted-foreground">No cover indicated</p>}
          </div>
          <div className="rounded-md border border-border p-3">
            <p className="text-[11px] font-semibold text-primary">Life / term cover</p>
            {li.has_cover ? <p className="mt-1 text-sm">{li.insurer || "?"} · {formatINR(li.sum_insured)}</p> : <p className="mt-1 text-sm text-muted-foreground">No cover indicated</p>}
          </div>
        </div>
      </Card>

      <Card className="border-border p-5" data-testid="booking-details">
        <h3 className="mb-3 flex items-center gap-2 font-dashboard text-sm font-bold"><Calendar className="h-4 w-4 text-primary" /> Booking details</h3>
        {primary ? (
          <div className="space-y-1 text-sm">
            <div><b>Slot:</b> {formatDate(primary.slot_datetime_utc)}</div>
            <div><b>Status:</b> {primary.status}</div>
            {primary.link_sent_at && <div><b>Link + instructions sent:</b> {formatDate(primary.link_sent_at)}</div>}
          </div>
        ) : <p className="text-sm text-muted-foreground">No appointment on file.</p>}
        {otherAppts.length > 0 && (
          <div className="mt-4 border-t border-border pt-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Other appointments</p>
            <ul className="space-y-1 text-xs text-muted-foreground">
              {otherAppts.map(a => (<li key={a.id}>{formatDate(a.slot_datetime_utc)} · {a.status}</li>))}
            </ul>
          </div>
        )}
      </Card>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="font-medium">{value || "—"}</p>
    </div>
  );
}
